import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export interface AnimatedContentProps {
  children: ReactNode;
  distance?: number;
  direction?: 'vertical' | 'horizontal';
  reverse?: boolean;
  duration?: number;
  ease?: string;
  initialOpacity?: number;
  animateOpacity?: boolean;
  scale?: number;
  threshold?: number;
  delay?: number;
  blur?: boolean | number;
  trigger?: 'scroll' | 'mount';
  once?: boolean;
  className?: string;
  style?: CSSProperties;
  onComplete?: () => void;
}

export function AnimatedContent({
  children,
  distance = 60,
  direction = 'vertical',
  reverse = false,
  duration = 0.85,
  ease = 'power3.out',
  initialOpacity = 0,
  animateOpacity = true,
  scale = 1,
  threshold = 0.15,
  delay = 0,
  blur = false,
  trigger = 'scroll',
  once = true,
  className = '',
  style,
  onComplete,
}: AnimatedContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect user's reduced motion preference
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1, filter: 'none', visibility: 'visible' });
      return;
    }

    const axis = direction === 'horizontal' ? 'x' : 'y';
    const offset = reverse ? -distance : distance;
    const blurValue = typeof blur === 'number' ? blur : blur ? 10 : 0;

    // Initial starting state
    gsap.set(el, {
      [axis]: offset,
      scale,
      opacity: animateOpacity ? initialOpacity : 1,
      filter: blurValue > 0 ? `blur(${blurValue}px)` : 'none',
      visibility: 'visible',
    });

    if (trigger === 'mount') {
      const anim = gsap.to(el, {
        [axis]: 0,
        scale: 1,
        opacity: 1,
        filter: 'blur(0px)',
        duration,
        ease,
        delay,
        onComplete,
      });

      return () => {
        anim.kill();
      };
    }

    // Scroll-triggered reveal
    const startPos = `top ${(1 - threshold) * 100}%`;
    const toggleActions = once ? 'play none none none' : 'play none none reverse';

    const anim = gsap.to(el, {
      [axis]: 0,
      scale: 1,
      opacity: 1,
      filter: 'blur(0px)',
      duration,
      ease,
      delay,
      scrollTrigger: {
        trigger: el,
        start: startPos,
        toggleActions,
        once,
      },
      onComplete,
    });

    return () => {
      anim.kill();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el) {
          st.kill();
        }
      });
    };
  }, [
    distance,
    direction,
    reverse,
    duration,
    ease,
    initialOpacity,
    animateOpacity,
    scale,
    threshold,
    delay,
    blur,
    trigger,
    once,
    onComplete,
  ]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        visibility: 'hidden', // Prevents FOUC before GSAP sets initial transform
      }}
    >
      {children}
    </div>
  );
}

export default AnimatedContent;
